/* Libraries */
import { ComponentType, ReactElement, ReactNode } from 'react';
import cn from 'classnames';
import Link from 'next/link';

/* Components */
import Either from '@components/Either';
import { Icon, Badge, BADGE_TYPES, ELEMENT_SIZE } from 'ht-styleguide';
import PhoneFooter from '../PhoneFooter';

/* Styles */
import styles from './menutemplate.module.scss';

/* types */
type CopyComponentProps = {
  text: string;
  isPrimary: boolean;
  showBadge?: boolean;
};

export type TMenuItemVariant = 'standard' | 'subCategoryType';

export type MenuItemProps = {
  text: string;
  link: string;
  nextLink?: boolean;
  isExternalLink: boolean;
  onClick: BaseAnyFunction;
  hideIcon: boolean;
  onBackClick: BaseAnyFunction;
  isPrimaryItem: boolean;
  showBadge: boolean;
  Component: ComponentType<any>;
  variant?: TMenuItemVariant;
  /* A denotion that gives us an idea of what link it is. For example, type="chat" */
  type?: string;
};

type ListItemProps = {
  children: ReactElement;
  className?: string;
};

type MenuTemplateProps = {
  headerItems: Array<any>;
  secondaryItems: Array<any>;
  slide?: boolean;
  visible?: boolean;
  dataTestId?: string;
  children?: ReactNode;
};

/**
  Simple list item component with predetermined styles
*/
const ListItem = ({ children, className = '' }: ListItemProps) => {
  const listItemStyles = cn(styles.menuItemWrapper, className);
  return <li className={listItemStyles}>{children}</li>;
};

/**
  Simple text component with predetermined styles.
*/
export const CopyComponent = ({ text, isPrimary, showBadge }: CopyComponentProps) => {
  const copyStyles = isPrimary ? 'h5' : 'p1';
  return (
    <div className={copyStyles}>
      {text}
      {showBadge && <Badge type={BADGE_TYPES.success} size={ELEMENT_SIZE.SMALL} classes="marginLeft-tiny1" children="New" />}
    </div>
  );
};

/**
  Wrapper for an item in the menu
*/
const MenuItem = ({
  Component,
  type,
  text,
  variant = 'standard',
  link,
  isExternalLink,
  onClick,
  onBackClick,
  hideIcon,
  isPrimaryItem,
  showBadge = false,
}: MenuItemProps) => {
  if (onClick) {
    const buttonStyles = cn('plainButton', styles.itemContent, styles.onClick);
    return (
      <ListItem>
        <button className={buttonStyles} onClick={onClick} type="button">
          <CopyComponent text={text} isPrimary={isPrimaryItem} />
          {!hideIcon && <Icon name="chevron-right" className={styles.chevron} />}
        </button>
      </ListItem>
    );
  }
  if (onBackClick) {
    const buttonStyles = cn('plainButton', styles.itemContent, styles.onBackClick);
    const chevronStyles = cn(styles.chevron, styles.left);

    return (
      <ListItem>
        <button className={buttonStyles} onClick={onBackClick} type="button">
          <Icon name="chevron-left" className={chevronStyles} />
          <CopyComponent text={text} isPrimary={isPrimaryItem} />
        </button>
      </ListItem>
    );
  }
  if (Component) {
    const linkStyles = cn('-no-decoration', styles.itemContent, styles.link);

    if (type === 'chat') {
      return (
        <ListItem>
          <Component classesContainer={cn(linkStyles, 'paddingY-small')}>
            <CopyComponent text={text} isPrimary={isPrimaryItem} />
          </Component>
        </ListItem>
      );
    }

    return (
      <ListItem>
        <a className={linkStyles}>
          <Component />
        </a>
      </ListItem>
    );
  }

  if (link) {
    const linkStyles = cn('-no-decoration', styles.itemContent, styles.link);

    return (
      <ListItem>
        <Either condition={isExternalLink}>
          <a href={link} className={linkStyles} target="_blank" rel="noopener noreferrer">
            <CopyComponent text={text} isPrimary={isPrimaryItem} />
          </a>
          <Link href={link} className={linkStyles}>
            <CopyComponent text={text} isPrimary={isPrimaryItem} showBadge={showBadge} />
          </Link>
        </Either>
      </ListItem>
    );
  }
  return (
    <ListItem className={cn(styles.itemContent, styles[variant])}>
      <CopyComponent text={text} isPrimary={isPrimaryItem} />
    </ListItem>
  );
};

/**
  Template to be used in the sidebar navigation menu

  headerItems/secondaryItems is an array of objects. The following is the expected values of an object.

  text - String - label of menu item
  link - String - link
  isExternalLink - Boolean - if link leads outside of HT
  onClick - Function - Function to trigger on click. Will also render the right chevron icon
  onBackClick - Function - Function to trigger on click. Will also render the left chevron icon

*/
const MenuTemplate = ({ headerItems = [], secondaryItems = [], slide, visible, dataTestId, children }: MenuTemplateProps) => {
  const wrapperStyles = cn(styles.templateWrapper, {
    [styles.slide]: slide,
    [styles.visible]: visible,
  });

  return (
    <div className={wrapperStyles} data-testid={dataTestId}>
      <div className={styles.linksContainer}>
        <ul className={styles.headerLinks}>
          {headerItems.map((item, i) => (
            <MenuItem key={i} {...item} isPrimaryItem />
          ))}
        </ul>
        <ul className={styles.scrollableSecondaryLinks}>
          {secondaryItems.map((item, i) => {
            if (!item.text) return null;
            return <MenuItem key={i} {...item} />;
          })}
          {children}
        </ul>
      </div>
      <PhoneFooter />
    </div>
  );
};

export default MenuTemplate;
